import { useState, useEffect } from 'react';
import type { GetPublicAuthConfigResponse } from '@insforge/shared-schemas';
import { useInsforge } from '../provider/InsforgeProvider';

/**
 * Hook to get all public authentication configuration (OAuth + Email) from Insforge backend
 *
 * **IMPORTANT: This hook should ONLY be used in SignIn and SignUp components.**
 *
 * This hook lazily fetches all public authentication configuration from the backend
 * only when the component mounts. Using it in other components will cause unnecessary
 * API calls on every page load.
 *
 * @returns Object containing OAuth providers, email auth config, and loading state
 * - `authConfig`: Email authentication configuration object with password rules
 * - `isLoaded`: Boolean indicating if the config has been fetched
 *
 * @example
 * ```tsx
 * // ✅ Correct usage - only in SignIn/SignUp components
 * function SignUp() {
 *   const { authConfig, isLoaded } = usePublicAuthConfig();
 *
 *   if (!isLoaded) return <div>Loading...</div>;
 *
 *   return (
 *     <div>
 *       <p>OAuth providers: {authConfig?.oauthProviders.length}</p>
 *       <p>Password min length: {authConfig?.passwordMinLength}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @requires Must be used within InsforgeProvider
 */
export function usePublicAuthConfig(): {
  authConfig: GetPublicAuthConfigResponse | null;
  isLoaded: boolean;
} {
  const { getPublicAuthConfig } = useInsforge();
  const [authConfig, setAuthConfig] = useState<GetPublicAuthConfigResponse | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    async function fetchConfig() {
      const result = await getPublicAuthConfig();
      if (result) {
        setAuthConfig(result);
      } else {
        console.error('[usePublicAuthConfig] Failed to get public auth config');
        setAuthConfig(null);
      }
      setIsLoaded(true);
    }

    void fetchConfig();
  }, [getPublicAuthConfig]);

  return { authConfig, isLoaded };
}
